/* eslint-disable react/prop-types */

import React, { useEffect, useRef, useState } from 'react';
import FileUploader, { FileDecoder } from 'api/FileUploadApi';
import { Form, Col, Modal, Button, Alert } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import { fetchAddressFromOla } from './Mapmodel/services/fetchAddressfromOla';
import { BsInfoCircleFill } from 'react-icons/bs';

const ImageUpload = ({ metadata, field, setValue, value }) => {
  const [uploadedFile, setUploadedFile] = useState(
    metadata.type === 'file' && (value || metadata.defaultValue)
  );
  const [showModal, setShowModal] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [location, setLocation] = useState(null);
  const [date, setDate] = useState(null);
  const apiKey = process.env.REACT_APP_OLA_API_KEY;

  // New state to track if location permission is granted
  const [isLocationPermissionGranted, setIsLocationPermissionGranted] =
    useState(false);
  const [showLocationAlert, setShowLocationAlert] = useState(false);
  // New state for permission alert modal
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Camera states
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isWebView, setIsWebView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const openModal = () => {
    if (
      metadata.uploadConfig.acceptedFileTypes.some(type =>
        ['.jpeg', '.png', '.jpg'].includes(type)
      )
    ) {
      setShowModal(true);
    }
  };

  // Detect if running in WebView and mobile
  useEffect(() => {
    const detectEnvironment = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;

      // Detect WebView
      const isWebViewEnvironment =
        /wv/.test(userAgent) || // Android WebView
        (/iPhone|iPad|iPod/.test(userAgent) &&
          /AppleWebKit/.test(userAgent) &&
          !/Safari/.test(userAgent)) || // iOS WebView
        window.ReactNativeWebView !== undefined ||
        window.webkit?.messageHandlers !== undefined;

      // Detect mobile device
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );

      setIsWebView(isWebViewEnvironment);
      setIsMobile(isMobileDevice);
    };

    detectEnvironment();
  }, []);

  useEffect(() => {
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { latitude, longitude } = position.coords;
            setCoordinates({ latitude, longitude });
            setIsLocationPermissionGranted(true);

            // Fetch the location address using API when coordinates are available
            fetchAddressFromOla(latitude, longitude, apiKey)
              .then(response => {
                const location = response?.address || 'Location not found';
                setLocation(location);
                setDate(new Date().toString());
              })
              .catch(error => console.error('Error fetching address:', error));
          },
          error => {
            console.error('Geolocation Error:', error);
            setIsLocationPermissionGranted(false);
          }
        );
      } else {
        console.log('Geolocation is not supported by this browser');
        setIsLocationPermissionGranted(false);
      }
    };

    getLocation();
  }, []);
  // Get permission instruction message based on device and error
  const getPermissionInstructions = () => {
    if (permissionError === 'location') {
      return 'Your location is off. Please enable Location services in your settings';
    } else if (permissionError === 'camera') {
      return 'Your camera is off. Please enable Camera services in your settings';
    }

    return 'Something Went Wrong , please try again later';
  };
  useEffect(() => {
    const processFile = async () => {
      // Check if uploadedFile is a key (not a URL/DataURL)
      const isKey =
        uploadedFile &&
        typeof uploadedFile === 'string' &&
        !uploadedFile.startsWith('data:') &&
        !uploadedFile.startsWith('blob:') &&
        !uploadedFile.startsWith('http');

      if (isKey) {
        try {
          const decryptUrl = await FileDecoder(uploadedFile);
          if (decryptUrl) {
            setUploadedFile(decryptUrl);
          }
        } catch (error) {
          console.error('File decoding failed:', error);
        }
      }
    };
    processFile();
  }, [uploadedFile]);

  const handleFileChange = async e => {
    try {
      const { file, url } = await FileUploader(e, metadata);
      setValue(metadata.id, url);
      setUploadedFile(url);
    } catch (error) {
      console.error('File upload failed:', error);
    }
  };

  const handleClearImage = () => {
    setUploadedFile(null);
    setValue(metadata.id, '');
  };

  const handleUploadFile = () => {
    fileInputRef.current.value = null; // Clear the input value to allow re-selecting the same file
    fileInputRef.current.click();
    setShowModal(false);
  };

  const requestLocationPermission = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async position => {
            try {
              const { latitude, longitude } = position.coords;
              setCoordinates({ latitude, longitude });
              setShowModal(false);

              // Fetch the location address
              const response = await fetchAddressFromOla(
                latitude,
                longitude,
                apiKey
              );
              const location = response?.address || 'Location not found';

              setLocation(location);
              setIsLocationPermissionGranted(true);
              setDate(new Date().toString());

              resolve(true); // Resolve when location permission is granted
            } catch (error) {
              console.error('Error fetching address:', error);
              reject(error);
            }
          },
          error => {
            console.error('Geolocation Error:', error);
            setIsLocationPermissionGranted(false);
            setShowPermissionModal(true);
            setPermissionError('location');
            setShowModal(false);
            reject(error); // Reject when location permission is denied
          }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  // Camera cleanup
  const stopCamera = () => {
    if (cameraStream) {
      const tracks = cameraStream.getTracks();
      tracks.forEach(track => {
        track.stop();
        console.log('Stopped camera track:', track.label);
      });
      setCameraStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
    setShowModal(false);
    setCameraError(null);
  };

  // Simplified camera starting function
  const startCamera = () => {
    return new Promise((resolve, reject) => {
      setCameraError(null);

      // Stop any existing stream
      if (cameraStream) {
        stopCamera();
      }

      // Determine camera constraints based on device type
      const constraints = {
        video: isMobile
          ? {
              facingMode: 'environment', // Always use back camera on mobile
              width: {
                ideal: isWebView ? 1280 : 1920,
                max: isWebView ? 1280 : 1920
              },
              height: {
                ideal: isWebView ? 720 : 1080,
                max: isWebView ? 720 : 1080
              },
              frameRate: { ideal: 30, max: 30 }
            }
          : {
              // For laptops/desktops, use default camera (usually front)
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30, max: 30 }
            },
        audio: false
      };
      console.log('Attempting to start camera with constraints:', constraints);

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(stream => {
          console.log('Camera stream obtained successfully');

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            setCameraStream(stream);

            // Add event listeners for video
            videoRef.current.onloadedmetadata = () => {
              console.log('Video metadata loaded');
              videoRef.current.play().catch(e => {
                console.error('Error playing video:', e);
              });
            };

            videoRef.current.oncanplay = () => {
              console.log('Video can play');
            };

            videoRef.current.onerror = e => {
              console.error('Video element error:', e);
              setCameraError('Video playback error');
            };

            resolve(stream);
          } else {
            console.error('Video ref is null');
            reject(new Error('Video element not available'));
          }
        })
        .catch(error => {
          console.error('getUserMedia error:', error);
          setCameraError(error.message);

          // Try fallback constraints for WebView
          if (isWebView) {
            console.log('Trying fallback constraints for WebView...');
            const fallbackConstraints = {
              video: {
                width: { ideal: 640 },
                height: { ideal: 480 },
                frameRate: { ideal: 15 }
              },
              audio: false
            };

            navigator.mediaDevices
              .getUserMedia(fallbackConstraints)
              .then(stream => {
                if (videoRef.current) {
                  videoRef.current.srcObject = stream;
                  setCameraStream(stream);
                  resolve(stream);
                }
              })
              .catch(fallbackError => {
                console.error('Fallback camera access failed:', fallbackError);
                reject(fallbackError);
              });
          } else {
            reject(error);
          }
        });
    });
  };

  const requestCameraPermission = () => {
    setIsCameraActive(true);
    setShowModal(false);
    return new Promise((resolve, reject) => {
      startCamera()
        .then(resolve)
        .catch(error => {
          console.error('Error accessing camera:', error);
          handleCameraError(error);
          reject(error);
        });
    });
  };

  // Camera error handler
  const handleCameraError = error => {
    stopCamera();
    setShowPermissionModal(true);
    setPermissionError('camera');
    setCameraError(error.message || 'Camera access failed');
  };

  const handleCameraCapture = async () => {
    try {
      setShowModal(false);

      // Step 1: Check location permission
      if (!isLocationPermissionGranted) {
        setShowLocationAlert(true); // Alert user about location requirement
        await requestLocationPermission();

        // After requesting, check again if permission was granted
        if (!isLocationPermissionGranted) {
          console.error('Location permission is required.');
          return; // Stop execution if location permission is not granted
        }
      }

      // Step 2: Check camera permission
      try {
        await requestCameraPermission(); // Request and activate the camera
      } catch (error) {
        console.error('Camera permission is required.');
        setShowPermissionModal(true);
        setPermissionError('camera');
        return; // Stop execution if camera permission is not granted
      }
    } catch (error) {
      stopCamera(); // Ensure the camera stops on any error
      console.error('Permission error:', error);
      setShowPermissionModal(true);
    }
  };

  // Picture taking with better error handling
  const handleTakePicture = () => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas) {
        console.error('Video or canvas element not available');
        setCameraError(
          'Unable to capture image - missing video or canvas element'
        );
        return;
      }

      // Check if video is actually playing
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error('Video dimensions are zero - camera may not be working');
        setCameraError('Camera not ready - please try again');
        return;
      }

      console.log(
        'Taking picture - video dimensions:',
        video.videoWidth,
        'x',
        video.videoHeight
      );

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const textPadding = 20;
      const strapHeight = 120;
      const maxWidth = canvas.width - textPadding * 2;
      const lineHeight = 24;

      context.fillStyle = 'rgba(0, 0, 0, 0.5)';
      context.fillRect(
        0,
        canvas.height - strapHeight,
        canvas.width,
        strapHeight
      );

      context.font = '18px Times New Roman';
      context.fillStyle = 'white';
      context.textAlign = 'left';

      const yStart = canvas.height - strapHeight + 30;
      let y = yStart;

      if (coordinates) {
        context.fillText(
          `Coordinates: ${coordinates.latitude}, ${coordinates.longitude}`,
          textPadding,
          y
        );
        y += lineHeight;
      }

      if (location) {
        const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
          const words = text.split(' ');
          let line = '';
          for (let i = 0; i < words.length; i++) {
            const testLine = `${line}${words[i]} `;
            const testWidth = context.measureText(testLine).width;
            if (testWidth > maxWidth && i > 0) {
              context.fillText(line, x, y);
              line = `${words[i]} `;
              y += lineHeight;
            } else {
              line = testLine;
            }
          }
          context.fillText(line, x, y);
        };

        wrapText(
          context,
          `Location: ${location}`,
          textPadding,
          y,
          maxWidth,
          lineHeight
        );
        y += lineHeight * 2;
      }

      if (date) {
        context.fillText(`Date: ${date}`, textPadding, y);
      }

      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      processCapture(imageData);
      stopCamera();
    } catch (error) {
      console.error('Error taking picture:', error);
      setCameraError('Failed to capture image: ' + error.message);
    }
  };

  const processCapture = async imageData => {
    if (imageData) {
      try {
        const byteString = atob(imageData.split(',')[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
          uintArray[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uintArray], { type: 'image/jpeg' });
        const file = new File([blob], 'captured-image.jpg', {
          type: 'image/jpeg'
        });

        const { file: uploadedFile, url } = await FileUploader(
          { target: { files: [file] } },
          metadata
        );

        setValue(metadata.id, uploadedFile);
        setUploadedFile(url);
      } catch (error) {
        console.error('File processing failed:', error);
      }
    }
  };

  const handleCloseCamera = () => {
    stopCamera();
    setShowButtons(true);
    setShowModal(false);
  };

  // Reset uploadedFile when value, metadata.id, or metadata.defaultValue changes
  useEffect(() => {
    setUploadedFile(
      metadata.type === 'file' && (value || metadata.defaultValue)
    );
  }, [value, metadata.id, metadata.defaultValue, metadata.type]);

  return (
    <Col>
      <Form.Group as={Col} sm={12} md={12} className="mb-3">
        <div className="d-flex justify-between items-center">
          <FormLabel
            label={metadata.label}
            required={metadata?.validation?.required}
          />
          <div
            style={{
              marginLeft: '5px',
              cursor: 'pointer',
              position: 'relative',
              display: 'inline-block'
            }}
            // onClick={() => setTooltipVisible(!tooltipVisible)}
            title={`Accepting file types: ${metadata.uploadConfig.acceptedFileTypes.join(
              ', '
            )}
            Max File Size: ${metadata.uploadConfig.maxFileSizeMB} MB`}
          >
            <BsInfoCircleFill size={18} />
            {/* {tooltipVisible && (
              <div
                style={{
                  position: 'absolute',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ccc',
                  padding: '5px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  zIndex: '1',
                  width: '210px',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                }}
              >
                {`Accepting file types: ${metadata.uploadConfig.acceptedFileTypes.join(
                  ', '
                )}
                Max File Size: ${metadata.uploadConfig.maxFileSizeMB} MB`}
              </div>
            )} */}
          </div>

          {/* Upload buttons only show if image not uploaded */}
          {!uploadedFile && (
            <div className="d-flex gap-2">
              {/* Web button */}
              <Button
                variant="primary"
                onClick={openModal}
                className="d-none d-md-block"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-file-earmark-image"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.502 7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                  <path d="M14 14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zM4 1a1 1 0 0 0-1 1v10l2.224-2.224a.5.5 0 0 1 .61-.075L8 11l2.157-3.02a.5.5 0 0 1 .76-.063L13 10V4.5h-2A1.5 1.5 0 0 1 9.5 3V1z" />
                </svg>
              </Button>

              {/* Mobile button */}
              <Button
                variant="primary"
                onClick={handleCameraCapture}
                className="d-block d-md-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-file-earmark-image"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.502 7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                  <path d="M14 14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zM4 1a1 1 0 0 0-1 1v10l2.224-2.224a.5.5 0 0 1 .61-.075L8 11l2.157-3.02a.5.5 0 0 1 .76-.063L13 10V4.5h-2A1.5 1.5 0 0 1 9.5 3V1z" />
                </svg>
              </Button>
            </div>
          )}
        </div>

        {/* Display WebView info and camera error if any */}
        {isWebView && (
          <Alert variant="info" className="mt-2 mb-2">
            <small>
              Running in WebView mode - camera functionality may be limited
            </small>
          </Alert>
        )}

        {cameraError && (
          <Alert variant="warning" className="mt-2 mb-2">
            <small>Camera Error: {cameraError}</small>
          </Alert>
        )}

        <Modal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {permissionError === 'location'
                ? 'Location Permission Required'
                : permissionError === 'camera'
                ? 'Camera Permission Required'
                : 'Error'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning" className="mb-0">
              <p>{getPermissionInstructions()}</p>
              {cameraError && (
                <small className="text-muted">
                  Technical details: {cameraError}
                </small>
              )}
            </Alert>
          </Modal.Body>
        </Modal>
        <div className="mb-3">
          {uploadedFile ? (
            // <div
            //   className="position-relative"
            //   style={{ width: '150px', height: '150px' }}
            // >
            //   <img
            //     src={uploadedFile}
            //     alt="Uploaded Preview"
            //     className="img-fluid"
            //     style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            //     onClick={() => {
            //       setFullScreenImage(uploadedFile);
            //       setShowFullScreen(true);
            //     }}
            //   />
            //   <button
            //     type="button"
            //     className="btn-close position-absolute"
            //     style={{ top: '5px', right: '5px', backgroundColor: 'white' }}
            //     aria-label="Close"
            //     onClick={handleClearImage}
            //   ></button>
            //   <a
            //     href={uploadedFile}
            //     download="downloaded_image.jpg"
            //     className="btn btn-primary position-absolute"
            //     style={{
            //       bottom: '5px',
            //       right: '5px',
            //       fontSize: '12px',
            //       padding: '5px',
            //       backgroundColor: 'rgba(78, 78, 78, 0.7)',
            //       color: 'white',
            //       border: 'none'
            //     }}
            //   >
            //     Download
            //   </a>
            // </div>
            <div
              style={{
                position: 'relative',
                marginLeft: '28px',
                marginTop: '6px',
                width: '36px',
                height: '36px'
              }}
            >
              <img
                src={uploadedFile}
                alt="Uploaded"
                onClick={() => {
                  setFullScreenImage(uploadedFile);
                  setShowFullScreen(true);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <button
                type="button"
                className="btn-close position-absolute"
                style={{
                  top: '1px',
                  right: '1px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  padding: '2px',
                  width: '8px',
                  height: '8px'
                }}
                aria-label="Clear image"
                onClick={handleClearImage}
              />
            </div>
          ) : null}
        </div>
      </Form.Group>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        accept={metadata.uploadConfig.acceptedFileTypes}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Modal for Upload or Camera Capture */}
      <Modal
        show={showModal}
        onHide={handleCloseCamera}
        style={{
          maxWidth: '100%',
          margin: 'auto'
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose Upload Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showButtons ? (
            <div className="d-flex justify-content-start gap-2">
              <Button
                variant="primary"
                onClick={handleUploadFile}
                className="mb-2"
              >
                Upload File
              </Button>
              <Button
                variant="primary"
                onClick={handleCameraCapture}
                className="mb-2"
              >
                Capture with Camera
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      {/* Camera Feed Modal */}
      <Modal show={isCameraActive} onHide={handleCloseCamera} centered>
        <Modal.Header closeButton>
          <Modal.Title>Camera Capture</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              width="100%"
              style={{ maxWidth: '1000px', maxHeight: '700px' }}
            ></video>
            <div>
              <Button
                variant="success"
                onClick={handleTakePicture}
                className="mt-2"
              >
                Take Picture
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Full Screen Image Modal */}
      <Modal
        show={showFullScreen}
        onHide={() => setShowFullScreen(false)}
        size="lg"
        centered
        style={{ maxWidth: '90%' }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Full Screen Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img
            src={fullScreenImage}
            alt="Full Screen Preview"
            className="img-fluid w-100 h-auto overflow-hidden"
            style={{ maxHeight: '60vh', objectFit: 'contain' }}
          />
        </Modal.Body>
      </Modal>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Col>
  );
};

export default ImageUpload;
